import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./api";

// Types for Betting API
export interface Bet {
  id: string;
  persona_id: string;
  sport: string;
  game: string;
  prediction: string;
  odds: string;
  source_message_id?: string;
  raw_text?: string;
  created_at: string;
  updated_at: string;
  status: string;
  result?: any;
  validation_status: string;
  validation_result?: {
    is_valid: boolean;
    bet_updates?: {
      sport?: string;
      game?: string;
      prediction?: string;
      odds?: string;
    };
    event_info?: {
      event_date?: string;
      event_status?: string;
      teams?: string[];
      league?: string;
      venue?: string;
    };
    outcome?: {
      result: "win" | "loss" | "pending" | "void";
      score?: string;
      explanation?: string;
    };
    sources?: Array<{
      name: string;
      url?: string;
    }>;
    raw_data?: {
      vertex_response: string;
      tavily_response?: any;
      processed_at: string;
    };
  };
  metadata?: {
    raw_text?: string;
    extracted_data?: any;
    [key: string]: any;
  };
  stake?: number; // Optional stake amount
}

export interface BetsListResponse {
  data: Bet[];
}

export interface UpdateBetResultRequest {
  status: string;
  result?: any;
}

export interface BackfillRequest {
  persona_id: string;
  batch_size?: number;
  validate?: boolean;
  limit?: number;
  days_back?: number;
  message_type?: string;
}

export interface ValidateBetsRequest {
  persona_id: string;
  bet_ids?: string[];
  batch_size?: number;
  days_back?: number;
}

export interface JobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface JobDetails {
  job_id: string;
  status: string;
  persona_id: string;
  type: string;
  results_summary?: any;
}

export interface BetStats {
  total_bets: number;
  pending_bets: number;
  won_bets: number;
  lost_bets: number;
  win_percentage: number;
  sports_distribution: {
    [sport: string]: {
      count: number;
      percentage: number;
    };
  };
  bet_types_distribution: {
    [betType: string]: {
      count: number;
      percentage: number;
    };
  };
  agents_performance: Array<{
    persona_id: string;
    total_bets: number;
    pending_bets: number;
    won_bets: number;
    lost_bets: number;
    win_percentage: number;
  }>;
}

export const getBetsByPersona = async (
  persona_id: string,
  limit: number = 1000,
  status?: string,
  validation_status?: string,
): Promise<BetsListResponse> => {
  try {
    const params: any = { limit };
    if (status) params.status = status;
    if (validation_status) params.validation_status = validation_status;

    const response = await apiClient.get(`/api/betting/persona/${persona_id}`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching bets for persona ${persona_id}:`, error);
    throw error;
  }
};

export const getAllBets = async (
  limit: number = 2000,
  status?: string,
  validation_status?: string,
): Promise<BetsListResponse> => {
  try {
    const params: any = { limit };
    if (status) params.status = status;
    if (validation_status) params.validation_status = validation_status;

    const response = await apiClient.get("/api/betting/all", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching all bets:", error);
    throw error;
  }
};

export const updateBetResult = async (
  persona_id: string,
  bet_id: string,
  request: UpdateBetResultRequest,
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.put(
      `/api/betting/${persona_id}/${bet_id}/result`,
      request,
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating bet result for ${bet_id}:`, error);
    throw error;
  }
};

export const validateBets = async (
  request: ValidateBetsRequest,
): Promise<JobResponse> => {
  try {
    const response = await apiClient.post("/api/betting/validate", request);
    return response.data;
  } catch (error) {
    console.error("Error starting validation job:", error);
    throw error;
  }
};

export const backfillBets = async (
  request: BackfillRequest,
): Promise<JobResponse> => {
  try {
    // Extract persona_id from the request object
    const { persona_id, ...bodyParams } = request;

    // Send persona_id as a query parameter, and the rest in the body
    const response = await apiClient.post(
      `/api/betting/backfill?persona_id=${encodeURIComponent(persona_id)}`,
      bodyParams,
    );

    return response.data;
  } catch (error) {
    console.error("Error starting backfill job:", error);
    throw error;
  }
};

// The mutation hook remains the same
export function useBackfillBetsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: backfillBets,
    onSuccess: () => {
      // Invalidate jobs list
      queryClient.invalidateQueries({
        queryKey: ["betting", "jobs"],
      });
    },
  });
}

export const getJobStatus = async (
  jobId: string,
): Promise<{
  job_id: string;
  status: string;
  persona_id: string;
  results: any;
}> => {
  try {
    const response = await apiClient.get(`/api/betting/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching job status for ${jobId}:`, error);
    throw error;
  }
};

export const listJobs = async (
  persona_id?: string,
  status?: string,
  limit: number = 10,
): Promise<{ jobs: JobDetails[] }> => {
  try {
    const params: any = { limit };
    if (persona_id) params.persona_id = persona_id;
    if (status) params.status = status;

    const response = await apiClient.get("/api/betting/jobs", { params });
    return response.data;
  } catch (error) {
    console.error("Error listing jobs:", error);
    throw error;
  }
};

export const getBettingStats = async (
  persona_id?: string,
  days: number = 30,
): Promise<BetStats> => {
  try {
    const params: any = { days };
    if (persona_id) params.persona_id = persona_id;

    const response = await apiClient.get("/api/betting/stats", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching betting stats:", error);
    throw error;
  }
};

export function useBetsByPersonaQuery(
  persona_id: string,
  limit: number = 1000,
  status?: string,
  validation_status?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["betting", persona_id, { limit, status, validation_status }],
    queryFn: () =>
      getBetsByPersona(persona_id, limit, status, validation_status),
    enabled: !!persona_id && enabled,
    refetchInterval: 300000, // Refetch every 5 minutes
  });
}

export function useAllBetsQuery(
  limit: number = 2000,
  status?: string,
  validation_status?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["betting", "all", { limit, status, validation_status }],
    queryFn: () => getAllBets(limit, status, validation_status),
    enabled: enabled,
    refetchInterval: 300000, // Refetch every 5 minutes
  });
}

export function useUpdateBetResultMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      persona_id,
      bet_id,
      status,
      result,
    }: {
      persona_id: string;
      bet_id: string;
      status: string;
      result?: any;
    }) => {
      return await updateBetResult(persona_id, bet_id, {
        status,
        result,
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate betting data for the persona
      queryClient.invalidateQueries({
        queryKey: ["betting", variables.persona_id],
      });
      // Also invalidate all bets query
      queryClient.invalidateQueries({
        queryKey: ["betting", "all"],
      });
      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: ["betting", "stats"],
      });
    },
  });
}

export function useValidateBetsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: validateBets,
    onSuccess: () => {
      // Invalidate jobs list
      queryClient.invalidateQueries({
        queryKey: ["betting", "jobs"],
      });
    },
  });
}

export function useJobStatusQuery(jobId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["betting", "jobs", jobId],
    queryFn: () => getJobStatus(jobId),
    enabled: !!jobId && enabled,
    refetchInterval: (data) => {
      // Refetch every 3 seconds if job is still running
      // @ts-expect-error Silencing error, but this is probably not working
      if (data?.status === "queued" || data?.status === "in_progress") {
        return 3000;
      }
      // Otherwise stop polling
      return false;
    },
  });
}

export function useJobsListQuery(
  persona_id?: string,
  status?: string,
  limit: number = 10,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["betting", "jobs", { persona_id, status, limit }],
    queryFn: () => listJobs(persona_id, status, limit),
    enabled: enabled,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
}

export function useBettingStatsQuery(
  persona_id?: string,
  days: number = 30,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["betting", "stats", { persona_id, days }],
    queryFn: () => getBettingStats(persona_id, days),
    enabled: enabled,
    refetchInterval: 300000, // Refetch every 5 minutes
  });
}
